import { useAccount, useMsal } from '@azure/msal-react';
import { Box } from '@mui/system';
import React, { createContext, FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { tokenRequest } from '../authConfig';
import { LogoSvgView } from '../components';
import { Routes } from '../constants';
import { getMe, postAuth } from '../fetch/user';
import {
  decodeJwt,
  getUserLocalStorage,
  removeUserLocalStorage,
  setUserLocalStorage,
} from '../helpers';
import { useToastContext } from '../hooks';
import { IAuthorizedUser, IAuthToken, IRoleType } from '../models';

interface IUserContext {
  setUserContext: (user: IAuthorizedUser | undefined) => void;
  userContext: IAuthorizedUser | undefined;
  isFetching: boolean;
}

export const UserContext = createContext<IUserContext>({
  setUserContext: () => {},
  userContext: undefined,
  isFetching: false,
});

interface IUserContextHandlerProps {
  children: React.ReactNode;
}

export const UserContextHandler: FC<IUserContextHandlerProps> = ({ children }) => {
  const [userContext, setUserContext] = useState<IAuthorizedUser | undefined>(undefined);
  const [isFetching, setFetching] = useState<boolean>(true);
  const { showToast } = useToastContext();
  const history = useHistory();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] ?? {});

  const setupUserContext = async () => {
    if (!account) return;

    setFetching(true);
    try {
      // Prefer getting auth from local over network
      let authToken: IAuthToken | undefined = (await getUserLocalStorage())?.authToken;

      if (!authToken || Date.parse(authToken.expiresAt) < Date.now()) {
        // Acquire token from authentication
        const msalToken = await instance
          .acquireTokenSilent({
            scopes: tokenRequest.scopes,
            account: account,
          })
          .then(authResult => authResult?.accessToken);

        if (!msalToken) return;

        // Call Frankie API to authorize the user, if necessary
        authToken = await postAuth(msalToken);
      }

      if (!authToken?.accessToken) return;

      // Now that user is authorized, get information about them
      const { fullName, featureFlags } = await getMe(authToken?.accessToken);

      if (!fullName) return;

      // Extract user roles from the JWT accessToken
      const decodedToken = decodeJwt<{
        EmployeeId: string;
        EmployeeRoles: IRoleType | IRoleType[];
      }>(authToken.accessToken);

      const roles: ReadonlySet<IRoleType> = new Set(
        !!decodedToken?.EmployeeRoles?.length
          ? ([] as IRoleType[]).concat(decodedToken.EmployeeRoles)
          : []
      );

      // Finally, save the authorized user
      await handleSetUser({
        authToken,
        employeeId: decodedToken?.EmployeeId,
        fullName,
        roles,
        featureFlags,
      });
    } catch (error) {
      history.push(Routes.noAccess);
      showToast('error', `${error}` || 'Could not successfully get user auth.');
    } finally {
      setFetching(false);
    }
  };

  // Handle log in and log out
  const handleSetUser = async (user: IAuthorizedUser | undefined) => {
    if (user) {
      setUserLocalStorage(user);
      setUserContext(user);
    } else {
      removeUserLocalStorage();
    }
  };

  useEffect(() => {
    setupUserContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This disallows routes from making calls with `authFetch` from rendered
  // routes before we are authenticated.
  // Show the Franklin Street logo while loading.
  if (isFetching) {
    return (
      <Box height="100vh" width="100vw" display="flex" justifyContent="center" alignItems="center">
        <LogoSvgView />
      </Box>
    );
  }

  return (
    <UserContext.Provider
      value={{
        userContext,
        setUserContext: handleSetUser,
        isFetching,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
